export interface WavInfo { channels: number; sampleRate: number; frames: number; duration: number; bits: number; format: number; dataOffset: number; dataLength: number; blockAlign: number }
const tag = (v: DataView, n: number) => String.fromCharCode(v.getUint8(n), v.getUint8(n + 1), v.getUint8(n + 2), v.getUint8(n + 3));
export function inspectWav(bytes: ArrayBuffer): WavInfo {
  const v = new DataView(bytes);
  if (v.byteLength < 44 || tag(v, 0) !== 'RIFF' || tag(v, 8) !== 'WAVE') throw new Error('Select an uncompressed RIFF WAV file.');
  const declared = v.getUint32(4, true) + 8;
  if (declared !== bytes.byteLength) throw new Error('WAV size does not match its header (incomplete or corrupt file).');
  let fmt: { channels: number; sampleRate: number; bits: number; format: number; blockAlign: number } | undefined;
  let dataOffset = 0, dataLength = 0;
  for (let pos = 12; pos + 8 <= declared;) {
    const name = tag(v, pos), size = v.getUint32(pos + 4, true), next = pos + 8 + size;
    if (next > declared) throw new Error('Truncated WAV chunk.');
    if (name === 'fmt ') {
      if (size < 16) throw new Error('Invalid WAV format chunk.');
      let format = v.getUint16(pos + 8, true);
      if (format === 0xfffe) {
        if (size < 40 || v.getUint16(pos + 24, true) < 22) throw new Error('Invalid extensible WAV header.');
        format = v.getUint16(pos + 32, true);
        const validBits = v.getUint16(pos + 26, true);
        if (validBits !== v.getUint16(pos + 22, true)) throw new Error('Packed extensible WAV valid-bits conversion is not supported in this spike.');
        const guidTail = [0, 0, 0, 0, 16, 0, 128, 0, 0, 170, 0, 56, 155, 113];
        if (guidTail.some((x, i) => v.getUint8(pos + 34 + i) !== x)) throw new Error('Unsupported extensible WAV subtype.');
      }
      fmt = { channels: v.getUint16(pos + 10, true), sampleRate: v.getUint32(pos + 12, true), blockAlign: v.getUint16(pos + 20, true), bits: v.getUint16(pos + 22, true), format };
      if (v.getUint32(pos + 16, true) !== fmt.sampleRate * fmt.blockAlign) throw new Error('Invalid WAV byte rate.');
    }
    if (name === 'data') { dataOffset = pos + 8; dataLength = size; }
    pos = next + (size % 2);
  }
  if (!fmt || !dataLength) throw new Error('Missing WAV format or audio data.');
  if (fmt.channels !== 2) throw new Error('This spike requires stereo stems.');
  if (![44100, 48000].includes(fmt.sampleRate)) throw new Error('Use 44.1 or 48 kHz WAV stems.');
  if (!(fmt.format === 1 && [16, 24, 32].includes(fmt.bits)) && !(fmt.format === 3 && fmt.bits === 32)) throw new Error('Use PCM 16/24/32-bit or float32 WAV.');
  if (fmt.blockAlign !== fmt.channels * fmt.bits / 8 || dataLength % fmt.blockAlign) throw new Error('Invalid WAV frame alignment.');
  const frames = dataLength / fmt.blockAlign;
  return { ...fmt, frames, duration: frames / fmt.sampleRate, dataOffset, dataLength };
}
export function decodeWav(bytes: ArrayBuffer, info = inspectWav(bytes)): Float32Array[] {
  const view = new DataView(bytes), channels = [new Float32Array(info.frames), new Float32Array(info.frames)];
  for (let i = 0; i < info.frames; i++) for (let ch = 0; ch < 2; ch++) {
    const at = info.dataOffset + i * info.blockAlign + ch * info.bits / 8;
    let sample: number;
    if (info.format === 3) sample = view.getFloat32(at, true);
    else if (info.bits === 16) sample = view.getInt16(at, true) / 32768;
    else if (info.bits === 32) sample = view.getInt32(at, true) / 2147483648;
    else { let n = view.getUint8(at) | view.getUint8(at + 1) << 8 | view.getUint8(at + 2) << 16; if (n & 0x800000) n -= 0x1000000; sample = n / 8388608; }
    if (!Number.isFinite(sample)) throw new Error('WAV contains a non-finite audio sample.');
    channels[ch][i] = sample;
  }
  return channels;
}
